import { EmployeeHoverCard } from "@/components/employee/EmployeeHoverCard.tsx";
import { EmployeeInline } from "@/components/employee/EmployeeInline.tsx";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import EmptyState from "@/components/empty-state/EmptyState.tsx";
import {
  ConfirmationModal,
  Page,
  useModals,
  Avatar,
  Button,
  DropdownMenu,
  ExpandableRow,
  FontAwesomeIcon,
  Stack,
  Table,
  TableCell,
} from "@skailer-com/uikit";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { buildTree } from "@/pages/settings/departments/departments.ts";
import { DepartmentEditModal } from "@/pages/settings/departments/widgets/DepartmentEditModal.tsx";
import { useDepartmentDelete, useDepartments } from "@/queries/use-departments.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { ApiDepartment } from "@/types/department.types.ts";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";

export default function Departments() {
  const { t } = useTranslation();

  useTitle(t("core:departments"), t("common:settings"));

  const { openModal } = useModals();
  const { data: departments, isLoading, isFetching } = useDepartments();
  const departmentDelete = useDepartmentDelete();

  const tableData = useMemo(() => {
    if (departments) {
      return buildTree(departments);
    }
    return [];
  }, [departments]);

  const handleEditOrCreate = useCallback((department: ApiDepartment | undefined = undefined) => {
    const departmentToEdit = department || {
      id: 0,
      title: "",
      emoji: "",
      parent_id: 0,
      employees_count: 0,
      direct_subordinates_count: 0,
      manager: null,
    };
    openModal(DepartmentEditModal, {
      department: departmentToEdit,
    });
  }, []);

  const handleDelete = useCallback((department: ApiDepartment) => {
    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("common:delete_confirm"),
      acceptTitle: t("common:delete"),
      onAccept: async function () {
        try {
          await departmentDelete.mutateAsync(department.id, {
            onSuccess: () => {
              analyticsService.trackEvent(analyticEvents.departments.deleted, {
                [analyticProperties.id]: department.id,
              });
            },
          });
        } catch {}
      },
    });
  }, []);

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.departments.viewed);
  });

  const renderRowAction = useCallback(
    (row: Row<ExpandableRow<ApiDepartment>>) => (
      <DropdownMenu>
        <Button
          onClick={() => handleEditOrCreate(row.original.item)}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen"} />}
          variant={"menu"}
        >
          {t("common:edit")}
        </Button>
        <Button
          onClick={() => handleDelete(row.original.item)}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
          variant={"menuDanger"}
        >
          {t("common:delete")}...
        </Button>
      </DropdownMenu>
    ),
    [handleDelete, handleEditOrCreate, t]
  );

  const columns = useMemo<ColumnDef<ExpandableRow<ApiDepartment>>[]>(
    () => [
      {
        header: t("core:department"),
        accessorKey: "title",
        ...ColumnSizes.employee,
        cell: ({ row }) => (
          <Stack
            direction={"horizontal"}
            className={"items-center"}
            gap={"sm"}
            style={{
              paddingLeft: `${row.depth}rem`,
            }}
          >
            <div>
              {row.getCanExpand() && (
                <button
                  {...{
                    onClick: row.getToggleExpandedHandler(),
                    style: { cursor: "pointer" },
                  }}
                >
                  {row.getIsExpanded() ? (
                    <FontAwesomeIcon icon={"fa-light fa-caret-down"} className={"w-4  h-4"} />
                  ) : (
                    <FontAwesomeIcon icon={"fa-light fa-caret-right"} className={"w-4  h-4"} />
                  )}
                </button>
              )}
            </div>

            <Avatar
              url={row.original.item.image_url}
              acronym={!row.original.item.emoji ? row.original.item.title : undefined}
              emoji={row.original.item.emoji}
              size={"md"}
            />

            <div>
              <TableCell>{row.original.item.title}</TableCell>
            </div>
          </Stack>
        ),
      },
      {
        header: t("core:manager"),
        accessorKey: "manager",
        ...ColumnSizes.employee,
        cell: ({ row }) =>
          row.original.item.manager && (
            <EmployeeHoverCard
              employee={row.original.item.manager}
              position={getEmployeePositionTitle(row.original.item.manager)}
            >
              <EmployeeInline employee={row.original.item.manager} />
            </EmployeeHoverCard>
          ),
      },
      {
        header: t("core:employees"),
        cell: (row) => <span>{row.row.original.item.employees_count}</span>,
        accessorKey: "employees_count",
        ...ColumnSizes.status,
      },
    ],
    [tableData]
  );
  return (
    <>
      <Page>
        <Page.Header
          showBack={true}
          title={t("core:departments")}
          navigateTo={"/settings/company-structure"}
        >
          <Stack>
            <Button
              onClick={() => {
                handleEditOrCreate();
              }}
              leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
            >
              {t("common:create_new")}
            </Button>
          </Stack>
        </Page.Header>
        <Page.Content>
          <Table
            renderRowAction={renderRowAction}
            data={tableData}
            isFetching={isFetching || departmentDelete.isPending}
            columns={columns}
            showSkeleton={isLoading}
            emptyStateComponent={<EmptyState />}
          />
        </Page.Content>
      </Page>
    </>
  );
}
